import type { User } from "@bezoned/shared/fluid/office-schema-factory.js";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { isDemoUserId } from "@bezoned/shared/utils/demo-users.js";
import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  FluentProvider,
  teamsLightTheme,
} from "@fluentui/react-components";
import type { PresenceBadgeStatus } from "@fluentui/react-components";
import { Html } from "@react-three/drei";
import type { Vector3 } from "three";

import { useAvatarMenu } from "../../../../hooks/use-avatar-menu.js";
import { useBezonedContext } from "../../../../hooks/use-bezoned-context.js";
import { useUserPhoto } from "../../../../hooks/use-user-photo.js";

interface UnseatedAvatarListProps {
  users: User[];
  offset: Vector3;
}

const SimplifiedAvatar = ({ user }: { user: User }) => {
  const isDummyUser = isDemoUserId(user.id);
  const { avatar } = useUserPhoto({ msUserId: user.id });
  const { showAvatarMenu } = useAvatarMenu();

  const handleClick = useStableCallback((event) => {
    event.stopPropagation();

    if (isDummyUser) {
      return;
    }

    showAvatarMenu(user.id, {
      x: event.clientX,
      y: event.clientY,
    });
  });

  const handleContextMenu = useStableCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  return (
    <AvatarGroupItem
      badge={{
        outOfOffice: user.presence?.outOfOffice?.isOutOfOffice,
        status: user.presence?.availability as PresenceBadgeStatus,
      }}
      data-office-avatar-overlay
      image={avatar}
      key={user.id}
      name={user.name}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
      style={{
        cursor: isDummyUser ? "default" : "pointer",
      }}
    />
  );
};

export const UnseatedAvatarList = ({
  users,
  offset,
}: UnseatedAvatarListProps) => {
  const { ContextBridge } = useBezonedContext();

  if (users.length === 0) {
    return null;
  }

  return (
    <Html
      position={offset}
      sprite
      style={{
        pointerEvents: "none",
        userSelect: "none",
      }}
      transform
    >
      <div
        data-blocks-hover="true"
        data-office-avatar-overlay
        style={{ pointerEvents: "auto" }}
      >
        <ContextBridge>
          <FluentProvider
            style={{ backgroundColor: "transparent" }}
            theme={teamsLightTheme}
          >
            <AvatarGroup size={48}>
              <AvatarGroupPopover
                openOnHover={true}
                tooltip={{
                  content: "",
                  relationship: "label",
                  visible: false,
                }}
              >
                {users.map((user) => (
                  <SimplifiedAvatar key={user.id} user={user} />
                ))}
              </AvatarGroupPopover>
            </AvatarGroup>
          </FluentProvider>
        </ContextBridge>
      </div>
    </Html>
  );
};
